import React, { Suspense, lazy, useState, useRef, useCallback, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPermissions } from "../rbac/hasAccess";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";

// Adjust these import paths to match where your components actually live
const EditEmployeeMaster = lazy(() => import("./editEmployeeMaster"));
const CurriculumVitaeForm = lazy(() => import("../curriculumVitae/curriculumVitaeForm"));
const EditJobDescriptionForm = lazy(() => import("../jobDescription/editJobDescriptionForm"));
const InterviewAssessmentForm = lazy(() => import("../interviewAssessment/interviewAssessmentForm"));
const EditTrainingFeedback = lazy(() => import("./TrainingFeedback/editTrainingFeedback"));
const EditAssetIssueRecord = lazy(() => import("./AssetIssueRecord/editAssetIssueRecord"));
const EditSurveyorMonitoringReport = lazy(() => import("./SurveyorMonitoringReport/EditSurveyorMonitoringReport"));
const EditNdtQualification = lazy(() => import("./NdtQualification/editNdtQualification"));
const EmpTraining = lazy(() => import("./emptraining/editEmpTraining"));
const VisualAcuityRecord = lazy(() => import("./visualAcuityRecord/editViewAcuityRecord"));
const IntegrityDeclaration = lazy(() => import("./integrityDeclaration/editIntegrityDeclaration"));
const InterviewPersonnel = lazy(() => import("./interviewPersonnel/editInterviewPersonnelForm"));
const TechnicalCompetencyRecord = lazy(() => import("./technicalCompetencyRecord/technicalCompetencyRecord"));
const OfferJoiningLetter = lazy(() => import("./offerJoiningLetter/editOfferJoiningLetter"));

export default function AllEmployeeForms(props) {
  const params = useParams();
  const navigate = useNavigate();
  const permissions = getPermissions(props.moduleId);

  const TABS = params?.id == 0 ? [{ key: "em", label: "Employee Master", Component: EditEmployeeMaster, moduleId: 21 },] : [
    { key: "em", label: "Employee Master", Component: EditEmployeeMaster, moduleId: 21 },
    { key: "cv", label: "Curriculum Vitae", Component: CurriculumVitaeForm, moduleId: 21 },
    { key: "jd", label: "Job Description", Component: EditJobDescriptionForm, moduleId: 21 },
    { key: "interview", label: "Interview Assessment", Component: InterviewAssessmentForm, moduleId: 21 },
    { key: "EditTrainingFeedback", label: "Training Feedback", Component: EditTrainingFeedback, moduleId: 21 },
    { key: "EditAssetIssueRecord", label: "Asset Issue Record", Component: EditAssetIssueRecord, moduleId: 21 },
    { key: "EditSurveyorMonitoringReport", label: "Surveyor Monitoring Report", Component: EditSurveyorMonitoringReport, moduleId: 21 },
    { key: "EditNdtQualification", label: "Ndt Qualification", Component: EditNdtQualification, moduleId: 21 },
    { key: "training", label: "Employee Training", Component: EmpTraining, moduleId: 21 },
    { key: "integrity", label: "Visual Acuity Record", Component: VisualAcuityRecord, moduleId: 21 },
    { key: "integrityDeclaration", label: "Integrity Declaration", Component: IntegrityDeclaration, moduleId: 21 },
    { key: "interviewPersonnel", label: "Interview Evaluation of Personnel", Component: InterviewPersonnel, moduleId: 21 },
    { key: "technicalCompetency", label: "Technical Competency Record", Component: TechnicalCompetencyRecord, moduleId: 21 },
    { key: "offerJoiningLetter", label: "Offer & Joining Letter", Component: OfferJoiningLetter, moduleId: 21 },
  ];

  const [activeTab, setActiveTab] = useState(TABS[0].key); // first tab loads by default
  const activeTabData = TABS?.find((tab) => tab.key === activeTab);
  const ActiveComponent = activeTabData?.Component;

  const trackViewportRef = useRef(null);
  const trackRef = useRef(null);
  const [trackOffset, setTrackOffset] = useState(0);
  const [maxTrackOffset, setMaxTrackOffset] = useState(0);

  const recalcTrackBounds = useCallback(() => {
    const viewport = trackViewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;
    const max = Math.max(0, track.scrollWidth - viewport.clientWidth);
    setMaxTrackOffset(max);
    setTrackOffset((prev) => Math.min(prev, max));
  }, []);

  useLayoutEffect(() => {
    recalcTrackBounds();
    const ro = new ResizeObserver(recalcTrackBounds);
    if (trackViewportRef.current) ro.observe(trackViewportRef.current);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("resize", recalcTrackBounds);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalcTrackBounds);
    };
  }, [recalcTrackBounds]);

  const toggleTrack = () => {
    if (trackOffset < maxTrackOffset) {
      setTrackOffset(maxTrackOffset); // slide all to right
    } else {
      setTrackOffset(0); // slide back to start
    }
  };

  return (
    <div className="w-full min-h-screen flex flex-col max-w-full overflow-hidden">
      {/* Tabs row with single slider toggle  */}
      <div className="flex items-center border-b border-gray-200 mb-2 shrink-0 px-2 bg-white">
        <div ref={trackViewportRef} className="flex-1 overflow-hidden">
          <div
            ref={trackRef}
            className="flex items-center justify-start lg:justify-center gap-1 sm:gap-2 transition-transform duration-300 ease-out select-none min-w-max"
            style={{ transform: `translateX(-${trackOffset}px)` }}
          >
            {TABS.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-1.5 sm:px-2 py-2 text-center text-xs sm:text-[13px] font-medium leading-tight flex flex-col justify-center items-center min-w-[70px] sm:min-w-[80px] max-w-[105px] border-b-2 -mb-px transition-colors ${isActive
                      ? "border-blue-600 text-blue-600 font-semibold"
                      : "border-transparent text-gray-500 hover:text-gray-800"
                    }`}
                >
                  <span className="w-full text-center break-words">
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Single slider toggle*/}
        {maxTrackOffset > 0 && (
          <button
            type="button"
            onClick={toggleTrack}
            className="flex-shrink-0 ml-2 h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 shadow-sm flex items-center justify-center transition-all duration-200"
            aria-label={trackOffset < maxTrackOffset ? "Slide all to right" : "Slide all to left"}
          >
            {trackOffset < maxTrackOffset ? (
              <MdKeyboardArrowRight className="h-4 w-4" />
            ) : (
              <MdKeyboardArrowLeft className="h-4 w-4" />
            )}
          </button>
        )}
      </div>

      {/* Active tab content container */}
      <div className="w-full flex-1 max-w-full overflow-x-auto">
        <Suspense fallback={<div className="text-center text-gray-400 py-10">Loading...</div>}>
          <div className="px-2 sm:px-4 py-1 max-w-full">
            {ActiveComponent && <ActiveComponent moduleId={activeTabData?.moduleId} />}
          </div>
        </Suspense>
      </div>
    </div>
  );
}